import { registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withNavigationErrorHandler,
} from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { resolveAppLocale } from './core/locale/app-locale';
import { provideLocaleAwareNativeDateAdapter } from './core/locale/locale-aware-native-date-adapter';
import { environment } from '../environments/environment';
import {
  ApiModule as DataApiModule,
  Configuration as DataApiConfig,
} from '../generated-sources/data-api';
import {
  ApiModule as AuthenticationApiModule,
  Configuration as AuthenticationApiConfig,
} from '../generated-sources/authentication-api';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
import { IconService } from './core/services/icon/icon.service';
import { SessionService } from './core/services/authentication/session.service';

registerLocaleData(localeEnGb);

const CHUNK_LOAD_RECOVERY_KEY = 'cgx_chunk_load_retry';
const APP_LOCALE = resolveAppLocale();

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    { provide: LOCALE_ID, useValue: APP_LOCALE },
    { provide: MAT_DATE_LOCALE, useValue: APP_LOCALE },
    ...provideLocaleAwareNativeDateAdapter(),
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withNavigationErrorHandler((error: unknown) => {
        handleNavigationError(error);
      }),
    ),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptors([httpErrorInterceptor])),
    provideAppInitializer(() => clearChunkLoadRecoveryFlag()),
    provideAppInitializer(() => inject(IconService).registerIcons()),
    provideAppInitializer(async () => {
      const session = inject(SessionService);
      await session.whenSessionReady();
      session.enforceProtectedEntry();
    }),
    importProvidersFrom(
      DataApiModule.forRoot(dataApiConfigFactory),
      AuthenticationApiModule.forRoot(userApiConfigFactory),
    ),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
  ],
};

export function dataApiConfigFactory(): DataApiConfig {
  return new DataApiConfig({
    basePath: environment.dataApiUrl,
    withCredentials: true,
  });
}

export function userApiConfigFactory(): AuthenticationApiConfig {
  return new AuthenticationApiConfig({
    basePath: environment.authenticationApiUrl,
    withCredentials: true,
  });
}

export function handleNavigationError(
  error: unknown,
  reload: () => void = () => window.location.reload(),
): void {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const isChunkLoadError =
    message.includes('dynamically imported module') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk');

  if (!isChunkLoadError || typeof window === 'undefined') {
    return;
  }

  if (sessionStorage.getItem(CHUNK_LOAD_RECOVERY_KEY) === '1') {
    sessionStorage.removeItem(CHUNK_LOAD_RECOVERY_KEY);
    return;
  }

  sessionStorage.setItem(CHUNK_LOAD_RECOVERY_KEY, '1');
  reload();
}

export function clearChunkLoadRecoveryFlag(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(CHUNK_LOAD_RECOVERY_KEY);
}
